import { useEffect, useRef, useState } from 'react';
import type { ClientBrandDto, MediaDto } from '@portfolio/shared';
import { cn } from '@/lib/utils';
import { Reveal } from '../Reveal';
import { SwipeTrack } from '../SwipeTrack';
import { useLocale } from '../i18n';
import { useScrollPin } from '../hooks';

const tileBase =
  'group flex h-[92px] w-full items-center justify-center rounded-[18px] transition-[transform,box-shadow,background] duration-500 sm:h-[110px] md:h-[125px]';
const tileDark =
  'bg-[linear-gradient(135deg,#1E1E1E_0%,#2E2E2E_45%,#7A7A7A_100%)] shadow-[0_30px_50px_-18px_rgb(30_30_30/0.55)]';
const tileLight = 'bg-tile hover:-translate-y-0.5 hover:bg-tile-hover';

/** Logos are tinted by CSS — grey on light tiles, white on the dark one — whatever their colours. */
function TileLogo({ c, dark }: { c: ClientBrandDto; dark: boolean }) {
  return c.logo ? (
    <img
      src={c.logo.url}
      alt={c.logo.alt ?? c.name}
      loading="lazy"
      decoding="async"
      className={cn(
        'max-h-[30px] w-auto max-w-[78%] object-contain brightness-0 transition-[filter] duration-500 md:max-h-[38px]',
        dark ? 'invert' : 'invert-[0.76] group-hover:invert-[0.55]',
      )}
    />
  ) : (
    <span
      className={cn(
        'text-lg font-bold transition-colors duration-500',
        dark ? 'text-white' : 'text-silver',
      )}
    >
      {c.name}
    </span>
  );
}

const artSrcSet = (m: MediaDto) =>
  m.thumbUrl && m.width ? `${m.thumbUrl} 720w, ${m.url} ${m.width}w` : undefined;

/**
 * One client-story slide ("Art Meets Ambition."). With uploaded artwork (the card design exported
 * without its text) the image is the background and the text is real HTML on top; without it,
 * the default dark card with the white stripe is drawn in code.
 */
function StorySlide({ c, index }: { c: ClientBrandDto; index: number }) {
  const label = c.storyLabel ?? c.name;
  const { t, rtl } = useLocale();
  // Arabic pages use the Arabic artwork (composed for text on the right) when the admin uploaded it
  const arArt = rtl ? c.storyImageAr : null;
  const desktopArt = arArt ?? c.storyImage;
  const phoneArt = (rtl && c.storyImageMobileAr) || (arArt ? null : c.storyImageMobile);
  const art = desktopArt ?? phoneArt;
  // Portrait artwork for phones and small tablets, when the admin uploaded one
  const mobileArt = desktopArt && phoneArt;
  // English artwork is composed for the footer row as laid out in English, so with it the number
  // and client name keep their sides on Arabic pages. The Arabic artwork and the default drawn
  // card mirror instead.
  const layoutDir = art && !arArt ? 'ltr' : t.dir;
  return (
    <article
      className={cn(
        'relative isolate w-full overflow-hidden rounded-[28px] px-6 pt-8 pb-7 text-white shadow-[0_40px_80px_-30px_rgb(30_30_30/0.45)] sm:px-10 md:rounded-[36px] md:px-[66px] md:pt-[44px] md:pb-[54px] lg:min-h-[504px]',
        art ? 'bg-night' : 'bg-[linear-gradient(180deg,#1C1C1C_0%,#2A2A2A_45%,#444444_100%)]',
      )}
    >
      {art ? (
        <>
          <picture>
            {mobileArt && (
              <source
                media="(min-width: 1024px)"
                srcSet={artSrcSet(art) ?? art.url}
                sizes="(min-width: 1208px) 1128px, 100vw"
                width={art.width ?? 2264}
                height={art.height ?? 1008}
              />
            )}
            <img
              src={(mobileArt ?? art).url}
              srcSet={artSrcSet(mobileArt ?? art)}
              sizes={mobileArt ? '100vw' : '(min-width: 1208px) 1128px, 100vw'}
              alt=""
              width={(mobileArt ?? art).width ?? 2264}
              height={(mobileArt ?? art).height ?? 1008}
              // every slide is one swipe away, so load them all up front (small WebP files)
              loading="eager"
              decoding="async"
              draggable={false}
              // phones: anchor the desktop artwork to the text side, where the designs keep the
              // background flat behind the text; a dedicated mobile artwork is composed to fit
              className={cn(
                'absolute inset-0 -z-10 size-full object-cover lg:object-center',
                mobileArt ? 'object-center' : arArt ? 'object-right-top' : 'object-left-top',
              )}
            />
          </picture>
          {/* Phones: the text covers more of the card, so keep it legible over the artwork */}
          <div
            aria-hidden
            className="absolute inset-0 -z-10 bg-gradient-to-b from-black/25 via-black/5 to-transparent lg:hidden"
          />
        </>
      ) : (
        <>
          {/* Default artwork: diagonal light stripe and the small square mark */}
          <svg
            aria-hidden
            viewBox="0 0 1132 504"
            preserveAspectRatio="none"
            className="absolute inset-0 -z-10 hidden h-full w-full lg:block rtl:-scale-x-100"
          >
            <path d="M88 504 L1132 128 L1132 150 L958 276 L362 504 Z" fill="#FFFFFF" />
          </svg>
          <svg
            aria-hidden
            viewBox="0 0 400 160"
            preserveAspectRatio="none"
            className="absolute inset-x-0 bottom-14 -z-10 h-[110px] w-full lg:hidden rtl:-scale-x-100"
          >
            <path d="M20 160 L400 20 L400 60 L130 160 Z" fill="#FFFFFF" />
          </svg>
          <svg
            aria-hidden
            viewBox="0 0 32 32"
            className="absolute end-[79px] top-[57px] -z-10 hidden size-8 border border-white lg:block rtl:-scale-x-100"
          >
            <path d="M5 21 L27 13" stroke="#fff" strokeWidth="1.2" />
          </svg>
        </>
      )}

      {/* Design grid: title 66–436px, paragraph 468–950px. Always follows the page direction, so
          on Arabic pages the title sits on the right and the paragraph on the left. */}
      <div
        dir={t.dir}
        className="grid gap-6 lg:grid-cols-[minmax(0,370px)_minmax(0,482px)] lg:gap-8"
      >
        <h3
          dir={t.dir}
          className={cn(
            // Arabic words are wider than the Latin ones, so the title is set a size smaller
            'text-[clamp(3rem,1.6rem+5vw,5.6rem)] leading-[0.92] font-extralight tracking-[-0.005em] whitespace-pre-line rtl:text-[clamp(2.4rem,1.4rem+3.4vw,4.25rem)]',
            art ? 'text-white/90' : 'text-[#D9D9D9]',
            // English artwork on Arabic pages: the title takes the right side, so clear the
            // artwork's icon in the top-right corner
            layoutDir !== t.dir && 'lg:me-12',
          )}
        >
          {c.storyTitle ?? c.name}
        </h3>
        {c.storyText && (
          <p
            dir={t.dir}
            className={cn(
              'text-body font-light lg:pt-2 lg:text-justify',
              art ? 'text-white/75' : 'text-[#BDBDBD]',
            )}
          >
            {c.storyText}
          </p>
        )}
      </div>

      <div
        dir={layoutDir}
        className="mt-28 flex items-end justify-between text-lead font-light text-white/80 lg:absolute lg:inset-x-[66px] lg:bottom-[54px] lg:mt-0"
      >
        <span className="tabular-nums">{String(index + 1).padStart(2, '0')}</span>
        {c.url ? (
          <a
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline-offset-4 hover:text-white hover:underline"
          >
            {label}
          </a>
        ) : (
          <span>{label}</span>
        )}
      </div>
    </article>
  );
}

/** Scroll distance (in viewport heights) spent on each client while the section is pinned. */
const STEP_VH = 0.6;

/**
 * Pins the section while the page scrolls through one client after another, then lets it go.
 * Returns the index the scroll position selects, and `goTo`, which scrolls to a client's stretch
 * so clicks, swipes and keys stay in step with scroll.
 */
function useClientSteps(count: number, enabled: boolean) {
  const [active, setActive] = useState(0);
  // While a click/swipe scrolls the page, ignore the slides passed on the way.
  const lock = useRef<{ index: number; timer: number } | null>(null);
  const { scrollTo, ...pin } = useScrollPin({
    enabled,
    distance: (vh) => count * STEP_VH * vh,
    onProgress: (p) => {
      const i = Math.min(count - 1, Math.floor(p * count));
      if (lock.current) {
        if (lock.current.index !== i) return;
        window.clearTimeout(lock.current.timer);
        lock.current = null;
      }
      setActive(i);
    },
  });

  const goTo = (i: number) => {
    setActive(i);
    if (!enabled) return;
    if (lock.current) window.clearTimeout(lock.current.timer);
    lock.current = { index: i, timer: window.setTimeout(() => (lock.current = null), 1500) };
    scrollTo((i + 0.5) / count);
  };

  return { ...pin, active, goTo };
}

/**
 * "Companies and projects that have trusted my services": the logo grid, and under it one dark
 * story slide per client. The section pins while the visitor scrolls, stepping through every
 * client from the first to the last before the page moves on; the active client's tile turns
 * dark. Tapping a tile or swiping the card scrolls to that client's stretch.
 */
export function Clients({
  heading,
  clients,
  showStories,
}: {
  heading: string;
  clients: ClientBrandDto[];
  showStories: boolean;
}) {
  const { t } = useLocale();
  const pinned = showStories && clients.length > 1;
  const { outer, inner, active, goTo, outerStyle, innerStyle } = useClientSteps(
    clients.length,
    pinned,
  );
  // Phones: the logos are one swipeable row, so the active logo and its story fit on screen
  // together; keep the active logo scrolled into the middle of the row.
  const row = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const ul = row.current;
    const tile = ul?.children[active] as HTMLElement | undefined;
    if (!ul || !tile || ul.scrollWidth <= ul.clientWidth) return;
    const r = tile.getBoundingClientRect();
    const box = ul.getBoundingClientRect();
    ul.scrollBy({ left: r.left + r.width / 2 - (box.left + box.width / 2), behavior: 'smooth' });
  }, [active]);
  if (!clients.length) return null;
  const fillers = (4 - (clients.length % 4)) % 4;

  return (
    <div ref={outer} style={outerStyle} className={cn(pinned && '-mb-8')}>
      <section
        ref={inner}
        aria-labelledby="clients-title"
        style={innerStyle}
        className={cn('container-site pt-14 md:pt-[48px]', pinned && 'sticky pb-8')}
      >
        <Reveal>
          <h2
            id="clients-title"
            className="heading-xl max-w-[640px] text-[clamp(2rem,1.2rem+3vw,3rem)] text-balance"
          >
            {heading}
          </h2>
        </Reveal>
        <ul
          ref={row}
          className="relative z-10 -mx-5 mt-8 flex snap-x snap-mandatory scroll-px-5 gap-3 overflow-x-auto px-5 [scrollbar-width:none] md:mx-0 md:mt-[55px] md:grid md:grid-cols-3 md:gap-[17px] md:overflow-visible md:px-0 lg:grid-cols-4 [&::-webkit-scrollbar]:hidden"
        >
          {clients.map((c, i) => {
            const dark = showStories ? i === active : c.highlighted;
            return (
              <Reveal
                as="li"
                key={c.id}
                delay={Math.min(i * 0.04, 0.3)}
                className="w-[42%] shrink-0 snap-start md:w-auto"
              >
                {showStories ? (
                  <button
                    type="button"
                    onClick={() => goTo(i)}
                    aria-pressed={i === active}
                    aria-controls="client-stories"
                    aria-label={t.showClientStory(c.name)}
                    className={cn(tileBase, dark ? tileDark : tileLight)}
                  >
                    <TileLogo c={c} dark={dark} />
                  </button>
                ) : c.url ? (
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={c.name}
                    className={cn(tileBase, dark ? tileDark : tileLight)}
                  >
                    <TileLogo c={c} dark={dark} />
                  </a>
                ) : (
                  <div className={cn(tileBase, dark ? tileDark : tileLight)}>
                    <TileLogo c={c} dark={dark} />
                  </div>
                )}
              </Reveal>
            );
          })}
          {Array.from({ length: fillers }).map((_, i) => (
            <li
              key={`f${i}`}
              aria-hidden
              className="hidden h-[125px] rounded-[18px] bg-tile lg:block"
            />
          ))}
        </ul>

        {showStories && (
          <Reveal className="pt-4 md:pt-[17px]">
            <div id="client-stories" aria-roledescription="carousel" aria-label={t.clientStories}>
              <SwipeTrack active={active} onChange={goTo} gap={140} gutter={100}>
                {clients.map((c, i) => (
                  <StorySlide key={c.id} c={c} index={i} />
                ))}
              </SwipeTrack>
            </div>
          </Reveal>
        )}
      </section>
    </div>
  );
}
